import React from "react";
import HUDFrame from "../components/HUDFrame";
import MedicalCareBadge from "../components/MedicalCareBadge";
import { Target, Eye, Zap, ShieldCheck } from "lucide-react";

export default function About({ onRequestDemo }) {
  return (
    <div className="relative z-10 pt-32 pb-20">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="hud-pill">ABOUT</div>
        <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-display font-black leading-[0.95]">
          <span className="text-white">Proprietary intelligence.</span>
          <br />
          <span className="text-green glow">Built for food &amp; agriculture.</span>
        </h1>
        <p className="mt-5 max-w-3xl text-lg text-[color:var(--ss-muted)]">
          StrainScan Pro is an enterprise-grade AI platform developed to raise
          the quality, consistency, and compliance standards of the modern food
          and agricultural supply chain. Powered by Medical Care Technologies
          Inc., we pair computer vision with domain-specific models trained on
          thousands of real-world product samples — from farm to distributor
          to retail shelf.
        </p>

        <div className="mt-12 grid lg:grid-cols-4 md:grid-cols-2 gap-5">
          {[
            {
              i: <Target />,
              t: "OUR MISSION",
              d: "Equip every operator — grower, distributor, restaurateur, logistics team — with lab-grade AI intelligence in seconds.",
            },
            {
              i: <Eye />,
              t: "OUR VISION",
              d: "A food and agricultural economy where every SKU is verified, documented, and trusted from origin to shelf.",
            },
            {
              i: <Zap />,
              t: "OUR EDGE",
              d: "Proprietary computer-vision models purpose-built for produce, packaged food, and specialty agriculture — not repurposed from other verticals.",
            },
            {
              i: <ShieldCheck />,
              t: "OUR STANDARD",
              d: "Enterprise security, tamper-evident audit trails, and compliance-ready workflows out of the box.",
            },
          ].map((x) => (
            <HUDFrame key={x.t} padding="p-6">
              <div className="w-12 h-12 border border-[rgba(157,252,4,0.4)] grid place-items-center text-[color:var(--ss-green)]">
                {x.i}
              </div>
              <div className="mt-4 font-display font-bold tracking-wide text-[color:var(--ss-green)]">
                {x.t}
              </div>
              <div className="mt-2 text-[color:var(--ss-muted)] text-sm leading-relaxed">
                {x.d}
              </div>
            </HUDFrame>
          ))}
        </div>

        <div className="mt-16 grid lg:grid-cols-[1.2fr_1fr] gap-6">
          <HUDFrame padding="p-8 md:p-12">
            <h2 className="text-2xl md:text-3xl font-display font-black">
              Why <span className="text-green glow">StrainScan Pro</span>
            </h2>
            <p className="mt-4 text-[color:var(--ss-muted)] leading-relaxed">
              The food and agricultural industries move product measured in
              billions of tons every year — yet quality still hinges on
              hand-graded samples, paperwork, and gut-feel. Our team set out
              to change that. StrainScan Pro ingests visual, chemical, and
              operational signals across every hand-off in the supply chain,
              giving operators the clarity to make better decisions in real
              time.
            </p>
            <p className="mt-4 text-[color:var(--ss-muted)] leading-relaxed">
              From the cultivation field to the receiving dock, the commercial
              kitchen, and the retail shelf, StrainScan Pro is trusted by
              operators who refuse to compromise on quality, consistency, or
              chain-of-custody proof.
            </p>

            <div className="mt-8 grid sm:grid-cols-3 gap-4">
              {[
                ["98.7%", "AVG RECOGNITION CONFIDENCE"],
                ["10K/hr", "IMAGES ANALYZED"],
                ["24/7", "CONTINUOUS AI LEARNING"],
              ].map(([v, l]) => (
                <div
                  key={l}
                  className="border border-[rgba(157,252,4,0.25)] p-4"
                >
                  <div className="text-3xl font-display font-black text-green glow">
                    {v}
                  </div>
                  <div className="text-[10px] tracking-[0.22em] font-display text-[color:var(--ss-muted)] mt-1">
                    {l}
                  </div>
                </div>
              ))}
            </div>
          </HUDFrame>

          <HUDFrame
            padding="p-8 md:p-10"
            className="flex flex-col items-center text-center"
          >
            <MedicalCareBadge />
            <div className="mt-5 text-[color:var(--ss-muted)]">
              StrainScan Pro is proudly developed and operated by
              <br />
              <span className="text-white">Medical Care Technologies Inc.</span>
            </div>
            <div className="mt-6 font-mono text-xs tracking-[0.3em] text-[color:var(--ss-green)]">
              (OTC TICKER: MDCE)
            </div>
            <button
              type="button"
              className="btn-primary mt-8"
              onClick={onRequestDemo}
            >
              Request Demo
            </button>
          </HUDFrame>
        </div>
      </div>
    </div>
  );
}
